/**
 * @file FontRenameDialog.tsx
 * @description «Переименовать» свой шрифт магазина (phase19.16 Р1-7; SB99):
 * название в списке шрифтов кабинета. В CSS имя не попадает (там `cf-<id>`),
 * поэтому на сайте ничего не меняется. Повтор имени — ошибка под полем.
 */

import { useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import { TextField } from "../TextField";
import { useShopFonts } from "../shop-fonts-context";
import { fontErrorCode, fontErrorText } from "../../../lib/site-view/shop-font-errors";
import type { ShopFontDto } from "../../../lib/site-view/shop-fonts";

interface Props {
  /** Шрифт; `null` — окно закрыто. */
  font: ShopFontDto | null;
  onClose: () => void;
}

export function FontRenameDialog({ font, onClose: close }: Props) {
  const shop = useShopFonts();
  const notify = useNotify();
  // Правка хранится вместе с id шрифта: для другого шрифта поле начинается с его названия.
  const [edit, setEdit] = useState<{ fontId: string; name: string; error: string | null } | null>(null);
  const [pending, setPending] = useState(false);

  const own = font !== null && edit?.fontId === font.id ? edit : null;
  const name = own?.name ?? font?.family ?? "";
  const error = own?.error ?? null;
  const trimmed = name.trim();
  const setName = (value: string) => font && setEdit({ fontId: font.id, name: value, error: null });
  const setError = (message: string) => font && setEdit({ fontId: font.id, name, error: message });
  const onClose = () => {
    setEdit(null);
    close();
  };

  async function save() {
    if (!font || !trimmed) return;
    setPending(true);
    try {
      await shop.rename(font.id, trimmed);
      notify.success(`Шрифт переименован в «${trimmed}»`);
      onClose();
    } catch (err) {
      if (fontErrorCode(err) === "FONT_FAMILY_TAKEN") setError(`Шрифт «${trimmed}» у вас уже есть — выберите другое название.`);
      else notify.error(fontErrorText(err, "Не удалось переименовать шрифт."));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={font !== null} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Переименовать шрифт</DialogTitle>
        </DialogHeader>
        <TextField
          label="Название шрифта"
          value={name}
          onChange={setName}
          max={60}
          hint="Так шрифт называется в списке. На сайте ничего не изменится."
          error={error}
        />
        <DialogFooter>
          <Button variant="ghost" disabled={pending} onClick={onClose}>
            Отмена
          </Button>
          <Button disabled={!trimmed || trimmed === font?.family} loading={pending} onClick={() => void save()}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
